import { ReactNode, useMemo } from 'react';

interface SimpleMarkdownPreviewProps {
  value: string;
}

// Very small markdown-to-React renderer supporting: headings, bold, italic, code, links, lists, blockquotes, paragraphs, line breaks.
// Intentionally avoids raw HTML for safety and renders only known markdown constructs.
export function SimpleMarkdownPreview({ value }: SimpleMarkdownPreviewProps) {
  const content = useMemo<ReactNode>(() => {
    if (!value || !value.trim()) return <p className="text-gray-500">Nothing to preview.</p>;

    // Split into lines and parse block-level constructs
    const lines = value.replace(/\r\n/g, '\n').split('\n');
    const elements: ReactNode[] = [];
    let listBuffer: { type: 'ul' | 'ol'; items: string[] } | null = null;

    const flushList = () => {
      if (!listBuffer) return;
      const items = listBuffer.items.map((item, idx) => <li key={`li-${idx}`}>{inline(item)}</li>);
      elements.push(
        listBuffer.type === 'ul' ?
          <ul key={`ul-${elements.length}`} className="list-disc pl-5 space-y-1">{items}</ul> :
          <ol key={`ol-${elements.length}`} className="list-decimal pl-5 space-y-1">{items}</ol>
      );
      listBuffer = null;
    };

    const inline = (text: string): ReactNode => {
      // Escape angle brackets to avoid HTML interpretation
      let escaped = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      // code `code`
      escaped = escaped.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-gray-100 rounded">$1</code>');
      // bold **text**
      escaped = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      // italic *text*
      escaped = escaped.replace(/\*([^*]+)\*/g, '<em>$1</em>');
      // links [text](url)
      escaped = escaped.replace(/\[([^\]]+)\]\((https?:[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>');
      // Convert to JSX via dangerouslySetInnerHTML for inline only (sanitized above)
      return <span dangerouslySetInnerHTML={{ __html: escaped }} />;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      if (!trimmed) {
        flushList();
        elements.push(<div key={`br-${i}`} className="h-2" />);
        continue;
      }

      // Headings
      const hMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
      if (hMatch) {
        flushList();
        const level = hMatch[1].length;
        const text = hMatch[2];
        const Tag = (`h${level}` as unknown) as keyof JSX.IntrinsicElements;
        elements.push(<Tag key={`h-${i}`} className="font-semibold text-gray-900 mt-2">{inline(text)}</Tag>);
        continue;
      }

      // Blockquote
      if (trimmed.startsWith('>')) {
        flushList();
        elements.push(
          <blockquote key={`q-${i}`} className="border-l-4 border-gray-300 pl-3 italic text-gray-700">
            {inline(trimmed.replace(/^>\s?/, ''))}
          </blockquote>
        );
        continue;
      }

      // Ordered list item
      const olMatch = trimmed.match(/^\d+\.\s+(.*)$/);
      if (olMatch) {
        if (!listBuffer || listBuffer.type !== 'ol') {
          flushList();
          listBuffer = { type: 'ol', items: [] };
        }
        listBuffer.items.push(olMatch[1]);
        continue;
      }

      // Unordered list item
      const ulMatch = trimmed.match(/^[-*+]\s+(.*)$/);
      if (ulMatch) {
        if (!listBuffer || listBuffer.type !== 'ul') {
          flushList();
          listBuffer = { type: 'ul', items: [] };
        }
        listBuffer.items.push(ulMatch[1]);
        continue;
      }

      // Paragraph
      flushList();
      elements.push(<p key={`p-${i}`} className="text-gray-800">{inline(trimmed)}</p>);
    }

    flushList();
    return <div className="space-y-2">{elements}</div>;
  }, [value]);

  return (
    <div className="prose prose-sm max-w-none">{content}</div>
  );
}


